import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Target,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  BookOpen,
  Briefcase,
  Brain,
  FileText,
  Download,
  RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';
import useUserStore from '../../store/userStore';
import api, { endpoints } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import StatsCard from '../../components/Cards/StatsCard';
import ProgressChart from '../../components/Charts/ProgressChart';
import SkillsChart from '../../components/Charts/SkillsChart';
import toast from 'react-hot-toast';

const PolicymakerDashboard = () => {
  const { user } = useUserStore();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch policymaker dashboard data
  const { data: dashboardData, isLoading, error, refetch } = useQuery(
    'policymaker-dashboard',
    async () => {
      const response = await api.get(endpoints.analytics.policymaker);
      return response.data;
    },
    {
      refetchInterval: 600000, // Refetch every 10 minutes
      onError: (error) => {
        toast.error('Failed to load dashboard data');
      }
    }
  );

  // Mock data for demonstration
  const mockStats = {
    totalLearners: 12500,
    totalTrainers: 450,
    totalCourses: 1200,
    skillsGapIdentified: 25,
    jobMarketTrends: 8.5,
    completionRate: 72,
    averageSalary: 650000,
    skillDemand: 85
  };

  const mockSkillsGap = [
    { skill: 'AI/ML', demand: 95, supply: 60, gap: 35 },
    { skill: 'Cloud Computing', demand: 90, supply: 70, gap: 20 },
    { skill: 'Data Science', demand: 85, supply: 55, gap: 30 },
    { skill: 'Cybersecurity', demand: 80, supply: 45, gap: 35 },
    { skill: 'Blockchain', demand: 75, supply: 40, gap: 35 },
  ];

  const mockRegionalData = [
    { region: 'North', learners: 3200, courses: 300, completion: 75 },
    { region: 'South', learners: 4100, courses: 450, completion: 78 },
    { region: 'East', learners: 2800, courses: 250, completion: 68 },
    { region: 'West', learners: 2400, courses: 200, completion: 72 },
  ];

  const mockTrends = [
    { month: 'Jan', enrollments: 1200, completions: 900 },
    { month: 'Feb', enrollments: 1350, completions: 1000 },
    { month: 'Mar', enrollments: 1500, completions: 1100 },
    { month: 'Apr', enrollments: 1650, completions: 1200 },
    { month: 'May', enrollments: 1800, completions: 1300 },
    { month: 'Jun', enrollments: 1950, completions: 1400 },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg">Failed to load dashboard</div>
        <p className="text-gray-600 mt-2">Please try refreshing the page</p>
        <button
          onClick={() => refetch()}
          className="btn btn-primary btn-sm mt-4"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  const stats = dashboardData?.stats || mockStats;
  const skillsGap = dashboardData?.skills_gap || mockSkillsGap;
  const regionalData = dashboardData?.regional_data || mockRegionalData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-accent-600 to-primary-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Policy Dashboard</h1>
            <p className="text-accent-100 mt-2">
              Monitor skills development trends and make data-driven policy decisions.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span className="text-sm font-medium">Real-time Data</span>
              </div>
              <p className="text-xs text-accent-100 mt-1">Updated every 10 minutes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Learners"
          value={stats.totalLearners.toLocaleString()}
          icon={Users}
          color="primary"
          trend="+5.2% this month"
        />
        <StatsCard
          title="Active Trainers"
          value={stats.totalTrainers}
          icon={BookOpen}
          color="secondary"
          trend="+12 this month"
        />
        <StatsCard
          title="Skills Gap Identified"
          value={stats.skillsGapIdentified}
          icon={AlertTriangle}
          color="warning"
          trend="+3 this week"
        />
        <StatsCard
          title="Avg Completion Rate"
          value={`${stats.completionRate}%`}
          icon={Target}
          color="success"
          trend="+2% this month"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'skills-gap', name: 'Skills Gap Analysis', icon: AlertTriangle },
            { id: 'regional', name: 'Regional Analysis', icon: MapPin },
            { id: 'trends', name: 'Market Trends', icon: TrendingUp },
            { id: 'reports', name: 'Reports', icon: FileText },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Learning Trends</h3>
                  <p className="card-description">Enrollment and completion trends over time</p>
                </div>
                <div className="card-content">
                  <ProgressChart data={mockTrends} />
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Skills Gap Overview</h3>
                  <p className="card-description">Critical skills requiring attention</p>
                </div>
                <div className="card-content">
                  <SkillsChart data={skillsGap.map(skill => ({
                    name: skill.skill,
                    level: skill.gap,
                    trend: skill.gap > 30 ? 'up' : 'stable'
                  }))} />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Policy Actions</h3>
                <p className="card-description">Take action based on insights</p>
              </div>
              <div className="card-content">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <AlertTriangle className="h-8 w-8 text-primary-600 mb-2" />
                    <h4 className="font-medium text-gray-900">Address Skills Gap</h4>
                    <p className="text-sm text-gray-600">Create targeted programs</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="p-4 border border-gray-200 rounded-lg hover:border-secondary-300 hover:bg-secondary-50 transition-colors"
                  >
                    <FileText className="h-8 w-8 text-secondary-600 mb-2" />
                    <h4 className="font-medium text-gray-900">Generate Report</h4>
                    <p className="text-sm text-gray-600">Export policy insights</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="p-4 border border-gray-200 rounded-lg hover:border-accent-300 hover:bg-accent-50 transition-colors"
                  >
                    <Target className="h-8 w-8 text-accent-600 mb-2" />
                    <h4 className="font-medium text-gray-900">Set Targets</h4>
                    <p className="text-sm text-gray-600">Define policy objectives</p>
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'skills-gap' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Skills Gap Analysis</h3>
                <p className="text-gray-600">Identify critical skills gaps and prioritize interventions</p>
              </div>
              <button className="btn btn-outline btn-sm">
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </button>
            </div>

            <div className="card">
              <div className="card-content">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Skill
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Demand
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Supply
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gap
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Priority
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {skillsGap.map((skill, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {skill.skill}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {skill.demand}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {skill.supply}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              skill.gap > 30 ? 'bg-red-100 text-red-800' : 
                              skill.gap > 20 ? 'bg-yellow-100 text-yellow-800' : 
                              'bg-green-100 text-green-800'
                            }`}>
                              {skill.gap}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              skill.gap > 30 ? 'bg-red-100 text-red-800' : 
                              skill.gap > 20 ? 'bg-yellow-100 text-yellow-800' : 
                              'bg-green-100 text-green-800'
                            }`}>
                              {skill.gap > 30 ? 'High' : skill.gap > 20 ? 'Medium' : 'Low'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'regional' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Regional Analysis</h3>
                <p className="text-gray-600">Skills development performance by region</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {regionalData.map((region, index) => (
                <motion.div
                  key={region.region}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card hover-lift"
                >
                  <div className="card-content">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{region.region}</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Learners:</span>
                          <span className="font-medium">{region.learners.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Courses:</span>
                          <span className="font-medium">{region.courses}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Completion:</span>
                          <span className="font-medium">{region.completion}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Market Trends</h3>
                <p className="text-gray-600">Job market and skills demand trends</p>
              </div>
            </div>

            <div className="card">
              <div className="card-content">
                <div className="text-center py-12">
                  <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Market Trends</h3>
                  <p className="text-gray-600">Advanced trend analysis coming soon</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Policy Reports</h3>
                <p className="text-gray-600">Generate and download policy reports</p>
              </div>
              <button className="btn btn-primary btn-sm">
                <FileText className="h-4 w-4 mr-2" />
                Generate Report
              </button>
            </div>

            <div className="card">
              <div className="card-content">
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Policy Reports</h3>
                  <p className="text-gray-600">Report generation features coming soon</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicymakerDashboard;
